import React, { useEffect, useState, useRef } from 'react';
import Hls from 'hls.js';
import './CatholicSGRadio.css';
import defaultArtwork from '../../../img/catholicsgradio/default_artwork.png';
import backgroundImage from '../../../img/catholicsgradio/radio.png';
import { useHistory } from 'react-router-dom';
import firebase from 'firebase/app';
import 'firebase/analytics';
import { v4 as uuidv4 } from "uuid";

// const AUDIO_STREAM = 'https://d29kzivdjpdtjq.cloudfront.net/live/48k/program2.m3u8';
// const AUDIO_STREAM = 'https://storage.googleapis.com/catholicsgradio-live-stream/live/48k/program2.m3u8';
// const METADATA_ENDPOINT = 'https://radio.eugenechriss.me/radio/metadata';
// const AUDIO_STREAM = 'https://radio.mycatholicradio.sg/live/48k/program2.m3u8'; // LATEST
const AUDIO_STREAM = 'https://radio.mycatholicradio.sg/stream/program2.m3u8';
// const METADATA_ENDPOINT = 'http://34.143.217.114:6000/radio/metadata';
const METADATA_ENDPOINT = 'https://metadata.mycatholicradio.sg/radio/metadata';

// ---- Session Tracking Utilities ----
const SESSION_KEY = "radio_session_uuid";
const SESSION_EXPIRY_SEC = 10 * 60;

function getSession() {
    try {
        const stored = JSON.parse(localStorage.getItem(SESSION_KEY));
        if (!stored) return null;
        const now = Date.now();
        if (now - stored.timestamp > SESSION_EXPIRY_SEC) {
            localStorage.removeItem(SESSION_KEY);
            return null; // expired
        }
        return stored.uuid;
    } catch {
        return null;
    }
}

function createNewSession() {
    const uuid = uuidv4();
    const timestamp = Date.now();
    localStorage.setItem(SESSION_KEY, JSON.stringify({ uuid, timestamp }));
    return uuid;
}

function updateSessionTimestamp() {
    try {
        const stored = JSON.parse(localStorage.getItem(SESSION_KEY));
        if (stored) {
            stored.timestamp = Date.now();
            localStorage.setItem(SESSION_KEY, JSON.stringify(stored));
        }
    } catch { }
}

const CatholicSGRadio = () => {

    const DEBUG_MODE = false;  // Debug flag - true for development, false for production or: process.env.NODE_ENV === 'development'
    const analytics = firebase.analytics();

    // Detect iframe
    const isEmbedded = window.self !== window.top;

    // mark when the page was (re)loaded so we can ignore early metadata briefly
    useEffect(() => {
        localStorage.setItem('pageReloadTime', Date.now().toString());
    }, []);

    const history = useHistory();
    const audioRef = useRef(null);
    const hlsRef = useRef(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(1);
    const [metadata, setMetadata] = useState({
        current_title: '',
        artist: '',
        artwork_url: '',
    });
    const [elapsedTime, setElapsedTime] = useState(0);

    const jumpToLive = () => {
        const audio = audioRef.current;
        if (!audio) return;

        // hls.js path (Chrome/Firefox/Edge, etc.)
        if (hlsRef.current && Number.isFinite(hlsRef.current.liveSyncPosition)) {
            try {
                audio.currentTime = hlsRef.current.liveSyncPosition;
            } catch { }
            return;
        }

        // Native HLS path (Safari/iOS): use seekable range end
        if (audio.seekable && audio.seekable.length > 0) {
            try {
                audio.currentTime = audio.seekable.end(audio.seekable.length - 1);
            } catch { }
        }
    };

    // Track time only when audio is playing
    useEffect(() => {
        let interval;
        if (isPlaying) {
            interval = setInterval(() => {
                setElapsedTime((prev) => prev + 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isPlaying]);

    // ⏱ Format elapsed time as mm:ss
    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    // Disable scroll on mount, re-enable on unmount
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    // Setup audio + HLS
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        if (Hls.isSupported()) {
            const hls = new Hls({
                // optional tuning:
                // liveSyncDurationCount: 3,
                // maxLiveSyncPlaybackRate: 1.0,
                // lowLatencyMode: true,
                // startPosition: -1,
            });
            hlsRef.current = hls;

            hls.loadSource(AUDIO_STREAM);
            hls.attachMedia(audio);

            // //console.log("[Audio] Attempting to initialise stereo context...");
            // try {
            //     const audioCtx = new (window.AudioContext || window.webkitAudioContext)({
            //         channelCount: 2,
            //         latencyHint: "interactive"
            //     });
            //     const source = audioCtx.createMediaElementSource(audio);
            //     source.channelCountMode = "explicit";
            //     source.channelInterpretation = "speakers";
            //     source.connect(audioCtx.destination);
            //     //console.log("[Audio] Stereo context initialised");
            // } catch (e) {
            //     //console.warn("[Audio] Context creation failed:", e);
            // }

            hls.on(Hls.Events.MANIFEST_PARSED, async () => {
                try {
                    jumpToLive(); // ensure we start at live edge
                    await audio.play();
                    setIsPlaying(true);
                } catch (err) {
                    console.warn('Autoplay failed:', err);
                }
            });
        } else if (audio.canPlayType('application/vnd.apple.mpegurl')) {
            // Native HLS (Safari)
            audio.src = AUDIO_STREAM;
            audio.addEventListener(
                'loadedmetadata',
                () => {
                    jumpToLive();
                },
                { once: true }
            );
            audio
                .play()
                .then(() => setIsPlaying(true))
                .catch((err) => console.warn('Autoplay failed:', err));
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, []);

    // Log current playback latency (difference between live edge and currentTime)
    useEffect(() => {
        const getLatencySec = () => {
            const audio = audioRef.current;
            if (!audio) return null;

            // hls.js path (Chrome/Edge/Firefox)
            if (hlsRef.current && Number.isFinite(hlsRef.current.liveSyncPosition)) {
                const liveEdge = hlsRef.current.liveSyncPosition;
                return Math.max(0, liveEdge - audio.currentTime);
            }

            // Native HLS (Safari)
            if (audio.seekable && audio.seekable.length > 0) {
                const liveEdge = audio.seekable.end(audio.seekable.length - 1);
                return Math.max(0, liveEdge - audio.currentTime);
            }

            return null;
        };

        const id = setInterval(() => {
            const latency = getLatencySec();
            if (latency != null) {
                console.log(`[CatholicSGRadio] Live latency ≈ ${latency.toFixed(1)}s`);
            }
        }, 5000); // every 5s

        return () => clearInterval(id);
    }, []);

    // Volume binding
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume;
        }
    }, [volume]);

    // Play/Pause control
    const togglePlayback = async () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            // === PAUSE ===
            try {
                audio.pause();
            } catch { }
            if (hlsRef.current) hlsRef.current.stopLoad();
            setIsPlaying(false);

            // Log pause with listen duration
            const sessionId = getSession();
            analytics.logEvent("radio_pause", {
                session_id: sessionId,
                listen_duration_seconds: elapsedTime,
                timestamp: Date.now(),
                is_embedded: isEmbedded,
            });
            if (DEBUG_MODE) console.log(`[Analytics] Logged pause: ${elapsedTime}s`);

        } else {
             // === PLAY ===
            if (hlsRef.current) {
                hlsRef.current.startLoad(-1);
            } else if (audio.src) {
                try {
                    audio.load();
                } catch { }
            }

            jumpToLive();

            try {
                await audio.play();
                setIsPlaying(true);

                // Session-based analytics logging
                let sessionId = getSession();
                if (!sessionId) {
                    sessionId = createNewSession();
                    
                    analytics.logEvent("radio_play", {
                        session_id: sessionId,
                        source: isEmbedded ? "CatholicSGRadio_Embed" : "CatholicSGRadio",  // ← DIFFERENTIATE
                        timestamp: Date.now(),
                        is_embedded: isEmbedded,
                    });

                    if (DEBUG_MODE) console.log("[Analytics] Logged new session:", sessionId);
                } else {
                    updateSessionTimestamp();
                }

            } catch (e) {
                console.warn('Play failed:', e);
            }
        }
    };

    // Metadata polling + adaptive delay
    useEffect(() => {
        let timeoutId;
        const firstFetchDone = { current: false };

        // tune these two:
        const EFFECTIVE_LEAD_SEC = 8;  // ~= audio_start - (metadata hits FastAPI → browser fetch)
        const MIN_HOLD_SEC = 3;   // never flip instantly, even if latency spikes

        // Rehydrate last applied to avoid "Unknown…" on refresh
        try {
            const saved = localStorage.getItem('lastAppliedMetadata');
            if (saved) setMetadata(JSON.parse(saved));
        } catch { }

        function applyMetadata(data) {
            const applied = {
                current_title: data.current_title || 'Unknown Title',
                artist: data.artist || 'Unknown Artist',
                artwork_url: data.artwork_url || defaultArtwork,
            };

            setMetadata(prev => {
                const isNew = applied.current_title && applied.current_title !== prev.current_title;
                if (isNew) setElapsedTime(0);
                return applied;
            });

            try { localStorage.setItem('lastAppliedMetadata', JSON.stringify(applied)); } catch { }
            localStorage.removeItem('nextMetadataApplyTime');
            localStorage.removeItem('nextMetadataTitle');
        }

        const measureLatencySec = () => {
            const audio = audioRef.current;
            // Prefer native seekable (works in Safari)
            if (audio && audio.seekable && audio.seekable.length > 0) {
                const liveEdge = audio.seekable.end(audio.seekable.length - 1);
                return Math.max(0, liveEdge - audio.currentTime);
            }
            // Fallback: hls.js
            if (hlsRef.current && Number.isFinite(hlsRef.current.liveSyncPosition)) {
                const liveEdge = hlsRef.current.liveSyncPosition;
                const t = audio?.currentTime ?? 0;
                return Math.max(0, liveEdge - t);
            }
            return 10; // safe default
        };

        const scheduleApply = (data, delaySec) => {
            const holdSec = Math.max(MIN_HOLD_SEC, delaySec); // <-- NEVER less than MIN_HOLD_SEC
            const applyAt = Date.now() + holdSec * 1000;

            try {
                localStorage.setItem('nextMetadataApplyTime', String(applyAt));
                localStorage.setItem('nextMetadataTitle', data.current_title || '');
            } catch { }

            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => applyMetadata(data), holdSec * 1000);
        };

        const fetchMetadata = async () => {
            try {
                // small stabilization after hard reload
                const reloadTime = parseInt(localStorage.getItem('pageReloadTime') || '0', 10);
                if (Date.now() - reloadTime < 5000) return;

                const res = await fetch(METADATA_ENDPOINT, { 
                    cache: 'no-store',
                    mode: 'cors',
                    headers: { 
                        'Accept': 'application/json',
                    }
                });
                
                if (!res.ok) {
                    console.warn(`Metadata fetch failed: ${res.status} ${res.statusText}`);
                    return;
                }
                const data = await res.json();
                console.log('Metadata:', data);

                // If we already scheduled a flip, honor it
                const storedTimeStr = localStorage.getItem('nextMetadataApplyTime');
                const storedTitle = localStorage.getItem('nextMetadataTitle') || '';
                if (storedTimeStr) {
                    const remainingMs = parseFloat(storedTimeStr) - Date.now();
                    if (remainingMs > 500) {
                        if ((data.current_title || '') === storedTitle) {
                            console.log(`[Metadata sync] restoring delayed apply in ${(remainingMs / 1000).toFixed(1)}s for "${storedTitle}"`);
                            clearTimeout(timeoutId);
                            timeoutId = setTimeout(() => applyMetadata(data), remainingMs);
                            return;
                        }
                        // different title than pending → clear and reschedule below
                        localStorage.removeItem('nextMetadataApplyTime');
                        localStorage.removeItem('nextMetadataTitle');
                    } else {
                        localStorage.removeItem('nextMetadataApplyTime');
                        localStorage.removeItem('nextMetadataTitle');
                    }
                }

                // What is currently shown?
                let lastApplied = null;
                try {
                    const raw = localStorage.getItem('lastAppliedMetadata');
                    lastApplied = raw ? JSON.parse(raw) : null;
                } catch { }

                const hasLast = !!(lastApplied && lastApplied.current_title);
                const sameTrack = hasLast && data.current_title && data.current_title === lastApplied.current_title;
                //const nextTrack = hasLast && data.current_title && data.current_title !== lastApplied.current_title;

                // Compute adaptive delay: (effective lead - measured latency), clamped by MIN_HOLD_SEC
                const latencySec = measureLatencySec();
                const desiredSec = EFFECTIVE_LEAD_SEC - latencySec;   // may be negative or very small
                // DO NOT allow 0 — clamp later in scheduleApply
                console.log(`[Metadata sync] latency=${latencySec.toFixed(1)}s → target hold ${(Math.max(desiredSec, MIN_HOLD_SEC)).toFixed(1)}s`);

                // 1) Cold start — never trust first payload immediately
                if (!firstFetchDone.current) {
                    firstFetchDone.current = true;
                    scheduleApply(data, desiredSec);
                    if (DEBUG_MODE) console.log(`[Metadata sync] COLD START → scheduling "${data.current_title}"`);
                    return;
                }

                // 2) If we still don't have a lastApplied (e.g., first apply still pending), keep holding
                if (!hasLast) {
                    scheduleApply(data, desiredSec);
                    if (DEBUG_MODE) console.log(`[Metadata sync] No lastApplied yet → scheduling "${data.current_title}"`);
                    return;
                }

                // 3) Same track → apply immediately (artwork/typo refresh)
                if (sameTrack) {
                    if (DEBUG_MODE) console.log(`[Metadata sync] Same track ("${data.current_title}") → apply immediately`);
                    applyMetadata(data);
                    return;
                }

                // 4) New track → NEVER apply immediately; schedule with clamp
                if (DEBUG_MODE) console.log(`[Metadata sync] NEXT TRACK early ("${data.current_title}") → schedule`);
                scheduleApply(data, desiredSec);

            } catch (err) {
                console.error('Metadata error:', err);
            }
        };

        fetchMetadata();
        const intervalId = setInterval(fetchMetadata, 10000);

        return () => {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };
    }, [DEBUG_MODE]);

    // Log final listen time on page unload
    useEffect(() => {
        const handleBeforeUnload = () => {
            const sessionId = getSession();
            if (sessionId && isPlaying && elapsedTime > 0) {
                // Log to Firebase Analytics
                analytics.logEvent("radio_close", {
                    session_id: sessionId,
                    final_duration_seconds: elapsedTime,
                    timestamp: Date.now(),
                });
                if (DEBUG_MODE) console.log(`[Analytics] Logged close: ${elapsedTime}s`);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isPlaying, elapsedTime, analytics, DEBUG_MODE]);

    return (
        <div
            className={`radio-page ${isEmbedded ? 'radio-embed' : ''}`}
            style={{ backgroundImage: `url(${backgroundImage})` }}
        >
            {/* Conditionally render header only if NOT embedded */}
            {!isEmbedded && (
                <div className="radio-header">
                    <button className="back-button" onClick={() => history.goBack()}>
                        ←
                    </button>
                    <span className="radio-title-header">CatholicSG Radio</span>
                </div>
            )}

            <div className="radio-content">
                <div className="now-playing-label">Now Playing</div>
                <div className="track-title">{metadata.current_title || 'Unknown Title'}</div>

                <img
                    src={metadata.artwork_url}
                    className="album-art-rounded"
                    alt="Album Art"
                    onError={(e) => {
                        e.currentTarget.src = defaultArtwork;
                    }}
                />

                <div className="track-artist">{'By ' + metadata.artist}</div>

                <div className="progress-container">
                    <span className="time-label">{formatTime(elapsedTime)}</span>

                    <div className="progress-bar live-animated-bar">
                        <div
                            className={`progress-fill-animated ${isPlaying ? 'playing' : 'paused'}`}
                        ></div>
                    </div>

                    <span className={`live-label ${isPlaying ? 'live' : 'paused'}`}>Live</span>
                </div>

                <button className="main-play-button play-button-spacing" onClick={togglePlayback}>
                    {isPlaying ? '❚❚' : '▶'}
                </button>

                <div className="volume-control">
                    <div className="volume-label">Volume</div>
                    <input
                        type="range"
                        className="volume-slider"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={(e) => setVolume(parseFloat(e.target.value))}
                    />
                </div>
            </div>

            <audio ref={audioRef} />
        </div>
    );
};

export default CatholicSGRadio;